'use client';

import { useMutation, useQuery } from '@tanstack/react-query';

import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';

import { GetMyProfileRes, GetMyProfileResError } from './types/profile';

export const useUserProfileQuery = (options?: {
  headers?: { authorization: string; 'x-id-token': string };
  idTokenPayload?: { [key: string]: unknown } | null;
}) => {
  const authContext = useAuth();
  const getAPIRequestTokenHeaders = authContext?.getAPIRequestTokenHeaders;
  const idTokenPayload = options?.idTokenPayload ?? authContext?.idTokenPayload;
  
  const roleIsBusiness = (idTokenPayload?.['custom:role'] === '1') === true;

  const endpoint = roleIsBusiness ? '/business-profiles/my' : '/profiles/my';
  const queryKey = roleIsBusiness ? 'business-profiles/my' : 'my-profile';

  const query = useQuery<GetMyProfileRes, GetMyProfileResError>({
    queryKey: [queryKey],
    queryFn: async () => {
      // Prefer explicit headers; otherwise the Axios interceptor attaches the
      // token from TokenService. We must NOT bail when no explicit headers are
      // available — that path is hit when this hook runs inside the Auth
      // provider itself (where useAuth() returns the default context), and
      // bailing there would leave the query unresolved forever.
      const headers = options?.headers ?? getAPIRequestTokenHeaders?.();
      try {
        const res = await Axios.get(endpoint, headers ? { headers } : undefined);
        return res.data;
      } catch (err: any) {
        // 404 means no profile exists — return empty response instead of throwing
        if (err?.response?.status === 404) {
          return { success: true, responseObject: null } as unknown as GetMyProfileRes;
        }
        throw err;
      }
    },
    enabled: !!idTokenPayload,
  });
  return query;
};

export const usePutUserProfileByIds = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  return useMutation<GetMyProfileRes, GetMyProfileResError, { ids: string[], status: string }>({
    mutationFn: async ({ ids, status }: { ids: string[], status: string }) => Axios.put(`/profiles`, { profileIDs: ids, status }, { headers: getAPIRequestTokenHeaders() }).then(res => res.data),
  });
};